import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { numberFormat } from 'shared/lib'
import { showToast } from 'shared/ui'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { SettingsPath } from 'entities/ExportRouters'
import { openIntercom } from 'entities/Intercom'
import { getBillingLabel, getIntervalMonthCount } from 'entities/Billing/lib/billingInterval'
import { AddOnModalStore } from 'pages/settings/ui/AddOnModal/store/addOnModalStore'
import { ModalActions } from 'pages/settings/pages/addOns/ui/BrandedUrlShortener/ModalActions/ModalActions'
import { ModalContent } from 'pages/settings/pages/addOns/ui/BrandedUrlShortener/ModalContent/ModalContent'

export class AddOnsStore {
  private readonly _addOnModalStore: AddOnModalStore
  private readonly _modalId = nanoid()
  private readonly _disableModalId = nanoid()
  private _loading = false

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)

    this._addOnModalStore = new AddOnModalStore(this._entities, 1)
  }

  init = async () => {
    this._loading = true

    try {
      await Promise.all([
        this._entities.billingStore.fetchBillingComplianceInfo(),
        !this._addOnModalStore.priceInfoStore.priceInfoUiData
          ? this._addOnModalStore.priceInfoStore.loadPriceData()
          : Promise.resolve(),
        this._entities.shortLinkStore.loadCustomDomain(),
      ])
    } finally {
      this._loading = false
    }
  }

  get entities() {
    return this._entities
  }

  get addOnModalStore() {
    return this._addOnModalStore
  }

  get loading() {
    return this._loading
  }

  get brandedUrlShortenerPriceText() {
    return this.getAddOnPriceText(20)
  }

  get trustedCallingPriceText() {
    return this.getAddOnPriceText(10)
  }

  private getAddOnPriceText(monthlyPrice: number) {
    const interval = this._entities.subscriptionStore.plan?.interval || 'monthly'
    const subtotal = getIntervalMonthCount(interval) * monthlyPrice

    return `${numberFormat({
      value: subtotal,
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} / ${getBillingLabel(interval)}`
  }

  openCallingAddOnModal = async () => {
    await this._addOnModalStore.openGetAddOnModal()
  }

  trustedCallingOnActionClick = () => {
    uiStore.changeRoute({
      path: `${SettingsPath.compliance.calling.root}`,
    })
  }

  trustedCallingOnContactSupportClick = () => {
    openIntercom()
  }

  brandedUrlShortenerOnActionClick = () => {
    this.closeBrandedUrlShortenerModal()
    uiStore.changeRouteViaVue({
      path: `/${SettingsPath.urlShortener.root}`,
    })
  }

  closeBrandedUrlShortenerModal = () => {
    modalStore.closeModal(this._modalId)
  }

  openBrandedUrlShortenerModal = () => {
    modalStore.addModal({
      id: this._modalId,
      type: ModalTypeList.DEFAULT,
      width: 540,
      title: 'Details',
      showCloseButton: false,
      ModalActions: () => <ModalActions store={this} />,
      ModalContent: () => <ModalContent store={this} />,
    })
  }

  deleteBrandedUrlShortenerModal = () => {
    modalStore.closeModal(this._modalId)
  }

  openDisableBrandedUrlShortenerModal = () => {
    modalStore.addModal({
      id: this._disableModalId,
      type: ModalTypeList.ALERT,
      width: 540,
      title: 'Disable Branded URL Shortener?',
      desc: 'Disabling this add-on will delete your URL Shortener setup. Previously shortened links will stop working. New shortened links will use the Salesmsg domain.',
      primaryAction: {
        text: 'Yes, disable',
        onAction: () => {
          this.handleDisableBrandedUrlShortener()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => {
          this.closeDisableBrandedUrlShortenerModal()
        },
      },
    })
  }

  closeDisableBrandedUrlShortenerModal = () => {
    modalStore.closeModal(this._disableModalId)
  }

  handleDisableBrandedUrlShortener = async () => {
    const isDeleted = await this._entities.shortLinkStore.deleteCustomDomain()

    this.closeDisableBrandedUrlShortenerModal()

    showToast({
      type: isDeleted ? 'success' : 'error',
      title: isDeleted
        ? 'Branded URL Shortener disabled'
        : 'Failed to disable Branded URL Shortener',
    })
  }

  brandedUrlShortenerOnSwitchChange = (value: boolean) => {
    if (!value) {
      this.openDisableBrandedUrlShortenerModal()
    }
  }
}
